import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Svg from './Svg';

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slidesToShow: this.getSlidesToShow(window.innerWidth),
      isAtStart: true,
      isAtEnd: false,
      gap: 15,
      isScrollable: false,
    };
    this.carouselRef = React.createRef();
    this.scrollbarRef = React.createRef();
    this.thumbRef = React.createRef();
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.carouselRef.current.addEventListener('scroll', this.handleScroll);
    this.updateScrollability();

    setTimeout(() => {
      this.initScrollbarDrag();
      this.updateThumbWidth();
    }, 0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.carouselRef.current.removeEventListener('scroll', this.handleScroll);
    this.removeScrollbarDrag();
  }

  getSlidesToShow = (width) => {
    const { responsive, slidesToShow, totalNumberOfSlides } = this.props;
    let slidesPerView =
      totalNumberOfSlides < slidesToShow ? totalNumberOfSlides : slidesToShow;

    if (!responsive || responsive.length === 0) return slidesPerView;

    for (let i = 0; i < responsive.length; i++) {
      const breakpoint = responsive[i];
      if (width <= breakpoint.breakpoint) {
        slidesPerView = breakpoint.settings.slidesToShow;
        break;
      }
    }

    return slidesPerView;
  };

  handleResize = () => {
    const slidesToShow = this.getSlidesToShow(window.innerWidth);
    this.setState({ slidesToShow }, () => {
      this.updateScrollability();
      this.updateThumbWidth();
      this.updateThumbPosition();
    });
  };

  scrollNext = () => {
    const { slidesToShow } = this.state;
    const itemWidth = this.carouselRef.current.clientWidth / slidesToShow;
    this.carouselRef.current.scrollBy({
      left: itemWidth + this.getGapOffset(),
      behavior: 'smooth',
    });
  };

  scrollPrev = () => {
    const { slidesToShow } = this.state;
    const itemWidth = this.carouselRef.current.clientWidth / slidesToShow;
    this.carouselRef.current.scrollBy({
      left: -itemWidth - this.getGapOffset(),
      behavior: 'smooth',
    });
  };

  getGapOffset = () => {
    const { slidesToShow, gap } = this.state;
    return (gap * (slidesToShow - 1)) / slidesToShow;
  };

  handleScroll = () => {
    this.updateThumbPosition();

    const scrollLeft = this.carouselRef.current.scrollLeft;
    const maxScrollLeft =
      this.carouselRef.current.scrollWidth -
      this.carouselRef.current.clientWidth;

    this.setState({
      isAtStart: scrollLeft === 0,
      isAtEnd: scrollLeft >= maxScrollLeft,
    });
  };

  updateThumbWidth = () => {
    if (!this.state.isScrollable) return;
    const totalSlides = React.Children.count(this.props.children);
    const { slidesToShow } = this.state;
    const trackWidth = this.scrollbarRef.current.clientWidth;
    const thumbWidth = (slidesToShow / totalSlides) * trackWidth;
    this.thumbRef.current.style.width = `${thumbWidth}px`;
  };

  updateThumbPosition = () => {
    if (!this.state.isScrollable) return;
    const scrollLeft = this.carouselRef.current.scrollLeft;
    const maxScrollLeft =
      this.carouselRef.current.scrollWidth -
      this.carouselRef.current.clientWidth;
    const trackWidth = this.scrollbarRef.current.clientWidth;
    const thumbWidth = this.thumbRef.current.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;
    const thumbLeft = (scrollLeft / maxScrollLeft) * maxThumbLeft || 0;
    this.thumbRef.current.style.left = `${thumbLeft}px`;
  };

  initScrollbarDrag = () => {
    if (!this.state.isScrollable) return;
    this.thumbRef.current.addEventListener('mousedown', this.onDragStart);
    document.addEventListener('mouseup', this.onDragEnd);
    document.addEventListener('mousemove', this.onDrag);
  };

  removeScrollbarDrag = () => {
    if (!this.state.isScrollable) return;
    this.thumbRef.current.removeEventListener('mousedown', this.onDragStart);
    document.removeEventListener('mouseup', this.onDragEnd);
    document.removeEventListener('mousemove', this.onDrag);
  };

  onDragStart = (e) => {
    this.isDragging = true;
    this.startX = e.clientX;
    this.scrollLeft = this.carouselRef.current.scrollLeft;
    e.preventDefault();
  };

  onDrag = (e) => {
    if (!this.isDragging) return;
    const dx = e.clientX - this.startX;
    const trackWidth = this.scrollbarRef.current.clientWidth;
    const thumbWidth = this.thumbRef.current.clientWidth;
    const maxScrollLeft =
      this.carouselRef.current.scrollWidth -
      this.carouselRef.current.clientWidth;
    const maxThumbLeft = trackWidth - thumbWidth;

    const scrollRatio = maxScrollLeft / maxThumbLeft;
    const scrollAmount = dx * scrollRatio;

    this.carouselRef.current.scrollLeft = this.scrollLeft + scrollAmount;
  };

  onDragEnd = () => {
    this.isDragging = false;
  };

  updateScrollability = () => {
    const isScrollable =
      this.carouselRef.current.scrollWidth >
      this.carouselRef.current.clientWidth;
    this.setState({ isScrollable });
  };

  render() {
    const { children } = this.props;
    const { slidesToShow, gap, isScrollable, isAtEnd, isAtStart } = this.state;

    return (
      <div className="content-carousel">
        <div className="content-carousel__container">
          <div
            className="content-carousel__slides-wrapper"
            ref={this.carouselRef}
          >
            <div className="content-carousel__slides">
              {React.Children.map(children, (child, index) => (
                <div
                  className="content-carousel__slide"
                  key={index}
                  style={{
                    flex: `0 0 calc(${100 / slidesToShow}% - ${
                      (gap * (slidesToShow - 1)) / slidesToShow
                    }px)`,
                  }}
                >
                  {child}
                </div>
              ))}
            </div>
          </div>
        </div>
        {isScrollable && (
          <>
            <div className="content-carousel__scrollbar-wrapper">
              <div
                className="content-carousel__scrollbar"
                ref={this.scrollbarRef}
              >
                <div
                  className="content-carousel__scrollbar-thumb"
                  ref={this.thumbRef}
                ></div>
              </div>
            </div>
            <div className="content-carousel__buttons">
              <button
                className="content-carousel__button"
                onClick={this.scrollPrev}
                data-disabled={isAtStart}
              >
                <Svg icon="arrow-left-thin" />
              </button>
              <button
                className="content-carousel__button"
                onClick={this.scrollNext}
                data-disabled={isAtEnd}
              >
                <Svg icon="arrow-right-thin" />
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
}

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  slidesToShow: PropTypes.number,
  responsive: PropTypes.arrayOf(
    PropTypes.shape({
      breakpoint: PropTypes.number.isRequired,
      settings: PropTypes.shape({
        slidesToShow: PropTypes.number.isRequired,
      }),
    })
  ),
  totalNumberOfSlides: PropTypes.number,
  gap: PropTypes.number,
};

Carousel.defaultProps = {
  slidesToShow: 3,
  responsive: [],
};

export default Carousel;
